/*
 * ProShield cart extras (drawer and cart page): cart timer, ship-by date, add-on buttons and
 * delivery protection toggle. Cart changes go through Shopify's AJAX cart and one
 * CartLinesUpdateEvent, so the Horizon drawer, cart page and header count refresh together.
 * This file never sets a price.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const SOURCE = 'ps-cart-plus';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
let busy = false;

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}
async function getCart() {
  const r = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) throw new Error('Could not read the cart');
  return r.json();
}
async function postJSON(url, body) {
  const r = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || data.status) throw new Error(data.description || data.message || 'The cart could not be updated');
  return data;
}
async function runUpdate(from, action, work) {
  const deferred = CartLinesUpdateEvent.createPromise();
  busy = true;
  from.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines: [], promise: deferred.promise }));
  try {
    const last = await work(sectionIds().join(','));
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: (last && last.sections) || undefined, source: SOURCE, didError: false },
    });
    if (window.PS && typeof window.PS.setCartCount === 'function') window.PS.setCartCount(cart.item_count);
    return cart;
  } catch (error) {
    let cart = null;
    try { cart = await getCart(); } catch (e) { /* keep the original error */ }
    if (cart) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: SOURCE, didError: true },
      });
    } else deferred.reject(error);
    throw error;
  } finally {
    busy = false;
  }
}

/* ---------- Timer ---------- */
const KEY = 'ps-cart-saved-until';
function pad(n) { return String(n).padStart(2, '0'); }
class CartTimer extends HTMLElement {
  connectedCallback() {
    const end = parseInt(this.dataset.end || '0', 10);
    if (end > 0) {
      this.until = end * 1000;
      this.long = true;
    } else {
      let until = 0;
      try { until = parseInt(sessionStorage.getItem(KEY) || '0', 10); } catch (e) { /* storage blocked */ }
      if (!until) {
        until = Date.now() + parseInt(this.dataset.minutes || '10', 10) * 60000;
        try { sessionStorage.setItem(KEY, String(until)); } catch (e) { /* storage blocked */ }
      }
      this.until = until;
    }
    this.tick();
    this.iv = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() { clearInterval(this.iv); }
  tick() {
    const t = this.querySelector('[data-t]');
    let s = Math.max(0, Math.floor((this.until - Date.now()) / 1000));
    if (this.long) {
      if (s <= 0) { this.hidden = true; clearInterval(this.iv); return; }
      const d = Math.floor(s / 86400); s -= d * 86400;
      const h = Math.floor(s / 3600); s -= h * 3600;
      const m = Math.floor(s / 60); s -= m * 60;
      if (t) t.textContent = (d ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
      return;
    }
    if (t) t.textContent = pad(Math.floor(s / 60)) + ':' + pad(s % 60);
    if (s <= 0) {
      this.querySelectorAll('[data-on]').forEach((el) => { el.hidden = true; });
      this.querySelectorAll('[data-off]').forEach((el) => { el.hidden = false; });
      this.classList.add('is-done');
      clearInterval(this.iv);
    }
  }
}

/* ---------- Ship-by date ---------- */
function addBusinessDays(date, n) {
  const d = new Date(date);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) added += 1;
  }
  return d;
}
const fmt = (d) => d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
class ShipDate extends HTMLElement {
  connectedCallback() {
    const now = new Date();
    const dispatch = parseInt(this.dataset.dispatch || '3', 10);
    const tmin = parseInt(this.dataset.tmin || '0', 10);
    const tmax = parseInt(this.dataset.tmax || '0', 10);
    const ship = addBusinessDays(now, dispatch);
    const set = (sel, txt) => { const el = this.querySelector(sel); if (el) el.textContent = txt; };
    set('[data-today]', fmt(now));
    set('[data-ship]', fmt(ship));
    if (tmax > 0) {
      const a = addBusinessDays(ship, Math.max(1, tmin));
      const b = addBusinessDays(ship, tmax);
      set('[data-arrive]', fmt(a) === fmt(b) ? 'Est. ' + fmt(b) : 'Est. ' + fmt(a) + ' – ' + fmt(b));
    }
  }
}

/* ---------- Add-ons ---------- */
class CartAddons extends HTMLElement {
  connectedCallback() { this.addEventListener('click', this.onClick); }
  disconnectedCallback() { this.removeEventListener('click', this.onClick); }
  onClick = async (event) => {
    const arrow = event.target instanceof Element ? event.target.closest('[data-dir]') : null;
    if (arrow) {
      const track = this.querySelector('[data-track]');
      const card = track && track.firstElementChild;
      if (track) track.scrollBy({ left: Number(arrow.dataset.dir) * ((card ? card.getBoundingClientRect().width : 260) + 10), behavior: 'smooth' });
      return;
    }
    const btn = event.target instanceof Element ? event.target.closest('[data-ps-add]') : null;
    if (!(btn instanceof HTMLButtonElement) || busy) return;
    const msg = this.querySelector('[data-msg]');
    const item = { id: Number(btn.dataset.psAdd), quantity: Math.max(1, parseInt(btn.dataset.qty || '1', 10)) };
    if (btn.dataset.for) item.properties = { For: btn.dataset.for };
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      await runUpdate(this, 'add', (sections) => postJSON(root + 'cart/add.js', { items: [item], sections }));
    } catch (e) {
      btn.disabled = false;
      btn.textContent = '+ Add';
      if (msg) msg.textContent = e.message || 'Sorry, that could not be added. Please try again.';
    }
  };
}

/* ---------- Delivery protection ---------- */
class CartProtect extends HTMLElement {
  connectedCallback() { this.addEventListener('change', this.onChange); }
  disconnectedCallback() { this.removeEventListener('change', this.onChange); }
  onChange = async (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || busy) return;
    const msg = this.querySelector('[data-msg]');
    const key = this.dataset.key || '';
    box.disabled = true;
    try {
      if (box.checked && !key) {
        await runUpdate(this, 'add', (sections) =>
          postJSON(root + 'cart/add.js', { items: [{ id: Number(this.dataset.variant), quantity: 1 }], sections }));
      } else if (!box.checked && key) {
        await runUpdate(this, 'remove', (sections) => postJSON(root + 'cart/update.js', { updates: { [key]: 0 }, sections }));
      }
    } catch (e) {
      box.checked = !box.checked;
      if (msg) msg.textContent = 'Sorry, that could not be changed. Please try again.';
    } finally {
      box.disabled = false;
    }
  };
}

if (!customElements.get('ps-cart-timer')) customElements.define('ps-cart-timer', CartTimer);
if (!customElements.get('ps-ship-date')) customElements.define('ps-ship-date', ShipDate);
if (!customElements.get('ps-cart-addons')) customElements.define('ps-cart-addons', CartAddons);
if (!customElements.get('ps-cart-protect')) customElements.define('ps-cart-protect', CartProtect);
